/**
 * Picks the given key(s) from an object, returning a new object with just the given keys
 * ```
 * pick({a: 'a', b: 'b', c: 'c'}, ['a', 'b'])
 * //=> {a: 'a', b: 'b'}
 * ```
 *
 * @param obj Subject to pick keys from
 * @param keys Keys to pick from object
 */
export const pick = <T extends Record<string, unknown>, K extends keyof T>(obj: T|null, keys: K|K[]): Pick<T, K> => {
    if (!(obj instanceof Object)) {
        return {} as T;
    }
    const res: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) {
        if (typeof keys === 'string' && k === keys || Array.prototype.includes.call(keys, k)) {
            res[k] = obj[k];
        }
    }
    return res as Pick<T, K>;
};

/**
 * Omits the given key(s) from an object, returning a new object without the given keys
 *
 * @param obj Subject to omit keys from
 * @param keys Keys to omit from object
 */
export const omit = <T extends Record<string, unknown>, K extends keyof T>(obj: T|null, keys: K|K[]): Omit<T, K> => {
    if (!(obj instanceof Object)) {
        return {} as T;
    }
    const res: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) {
        if (typeof keys === 'string' && k === keys || Array.prototype.includes.call(keys, k)) {
            continue;
        }
        res[k] = obj[k];
    }
    return res as Omit<T, K>;
};

/**
 * Polyfills Object.prototype.fromEntries
 */
export const fromEntries = <K extends string|number, V = unknown>(entries: Array<[K, V]>): Record<K, V> =>
    entries.reduce((c, [ key, value ]) => {
        c[key] = value;
        return c;
    }, {} as Record<K, V>);

/**
 * Given a props-like object with arbitrary values and a mapping of callbacks, these are executed for the object.
 *
 * This is most likely meant to be used with initializing local state from props:
 *
 * `handleObjectEntries(props.initialValues, { foo: setFoo, bar: setBar })`
 */
export const handleObjectEntries = <
    T extends Record<string, unknown>,
    K extends string & keyof T
>(subject: T, mapping: {[E in keyof T]?: (arg: NonNullable<T[E]>) => void}): void => {
    Object.entries(mapping).forEach(([ property, callback ]) => {
        if (subject[property]) {
            (callback as (arg: T[K]) => void)(subject[property] as T[K]);
        }
    });
};
